import { validateCnpj, validateCpf } from '@/helpers'
import { z } from 'zod'
import {} from 'date-fns'

export const partnerFormSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Obrigatório')
      .refine(
        (value) => /\S+\s+\S+/.test(value),
        'Informe o nome completo (nome e sobrenome)',
      ),
    tax_id: z
      .string()
      .min(1, 'Obrigatório')
      .refine(validateCpf, 'CPF inválido'),
    admission_date: z
      .date()
      .nullable()
      .refine((value) => value, 'Obrigatório'),
    pis: z.string().optional(),
    pro_labore_type: z.string().min(1, 'Obrigatório'),
    pro_labore_amount: z
      .string()
      .optional()
      .refine(
        (value) => {
          if (!value) return true
          const numeric = Number(
            value.replace(/[^\d,.-]/g, '').replace(',', '.'),
          )
          return numeric >= 1621
        },
        {
          message: 'O valor mínimo é R$ 1.621,00',
        },
      ),
    has_double_bond: z.union([
      z.boolean(),
      z.string().refine((val) => val !== '', 'Obrigatório'),
    ]),
    inss_base_amount: z.string().optional(),
    inss_contribution_amount: z.string().optional(),
    dual_social_security_indicator: z.string().optional(),
    second_cnpj_link: z.string().optional(),
    category_code: z.string().optional(),
  })
  .superRefine(({ pro_labore_type, pro_labore_amount }, ctx) => {
    if (pro_labore_type === 'fixed' && !pro_labore_amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Obrigatório',
        path: ['pro_labore_amount'],
      })
    }
  })
  .superRefine(
    (
      {
        has_double_bond,
        inss_base_amount,
        inss_contribution_amount,
        dual_social_security_indicator,
        second_cnpj_link,
        category_code,
      },
      ctx,
    ) => {
      if (has_double_bond === true) {
        if (!inss_base_amount) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Obrigatório',
            path: ['inss_base_amount'],
          })
        }

        if (!inss_contribution_amount) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Obrigatório',
            path: ['inss_contribution_amount'],
          })
        }

        if (!dual_social_security_indicator) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Obrigatório',
            path: ['dual_social_security_indicator'],
          })
        }

        if (!second_cnpj_link) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Obrigatório',
            path: ['second_cnpj_link'],
          })
        }

        if (second_cnpj_link && !validateCnpj(second_cnpj_link)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'CNPJ inválido',
            path: ['second_cnpj_link'],
          })
        }

        if (!category_code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Obrigatório',
            path: ['category_code'],
          })
        }
      }
    },
  )

export type PartnerFormData = z.infer<typeof partnerFormSchema>

export const partnerDefaultValues: PartnerFormData = {
  name: '',
  tax_id: '',
  admission_date: null,
  pis: '',
  pro_labore_type: '',
  pro_labore_amount: '',
  has_double_bond: '',
  inss_base_amount: '',
  inss_contribution_amount: '',
  dual_social_security_indicator: '',
  second_cnpj_link: '',
  category_code: '',
}
